"use client";

import React, { useState, useEffect } from "react";
import { Coordinates } from "@/lib/locationData";
import { useLocation } from "@/contexts/LocationContext";

export type DistanceDisplayProps = {
  coordinates?: Coordinates;
  distance?: number;
  variant?: "badge" | "text";
  className?: string;
  showIcon?: boolean;
  useEnhancedCalculation?: boolean; // Option to use Google Maps Distance Matrix
  mode?: "driving" | "walking" | "bicycling" | "transit";
};

/**
 * Displays distance in a user-friendly format (hours/minutes away)
 *
 * Can either take pre-calculated distance or coordinates to calculate distance from user's location
 * Supports both legacy Haversine calculation and enhanced Google Maps Distance Matrix calculation
 */
export default function DistanceDisplay({
  coordinates,
  distance: providedDistance,
  variant = "badge",
  className = "",
  showIcon = false,
  useEnhancedCalculation = true,
  mode = "driving",
}: DistanceDisplayProps) {
  const { userLocation, calculateDistance, calculateDistanceAsync } =
    useLocation();
  const [calculatedDistance, setCalculatedDistance] = useState<
    number | undefined
  >(providedDistance);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationSource, setCalculationSource] = useState<
    "provided" | "haversine" | "google" | null
  >(providedDistance !== undefined ? "provided" : null);

  useEffect(() => {
    // If distance is already provided, use it
    if (providedDistance !== undefined) {
      const timer = window.setTimeout(() => {
        setCalculatedDistance(providedDistance);
        setCalculationSource("provided");
      }, 0);
      return () => window.clearTimeout(timer);
    }

    // If no coordinates or user location, we can't calculate
    if (!coordinates || !userLocation?.coordinates) {
      const timer = window.setTimeout(() => {
        setCalculatedDistance(undefined);
        setCalculationSource(null);
      }, 0);
      return () => window.clearTimeout(timer);
    }

    const calculateDistanceValue = async () => {
      setIsCalculating(true);

      try {
        if (useEnhancedCalculation) {
          // Try enhanced calculation with Google Maps Distance Matrix
          const distance = await calculateDistanceAsync(
            userLocation.coordinates,
            coordinates,
            mode,
          );
          setCalculatedDistance(distance);
          setCalculationSource("google");
        } else {
          // Use legacy Haversine calculation
          const distance = calculateDistance(
            userLocation.coordinates,
            coordinates,
          );
          setCalculatedDistance(distance);
          setCalculationSource("haversine");
        }
      } catch (error) {
        console.warn(
          "[DISTANCE_DISPLAY] Enhanced calculation failed, falling back to Haversine:",
          error,
        );
        // Fallback to Haversine
        const distance = calculateDistance(
          userLocation.coordinates,
          coordinates,
        );
        setCalculatedDistance(distance);
        setCalculationSource("haversine");
      } finally {
        setIsCalculating(false);
      }
    };

    calculateDistanceValue();
  }, [
    coordinates,
    userLocation,
    providedDistance,
    calculateDistance,
    calculateDistanceAsync,
    useEnhancedCalculation,
    mode,
  ]);

  // If still calculating or no distance available, don't render
  if (isCalculating) {
    return (
      <span
        className={`bg-tertiary text-text-light text-xs font-medium px-1.5 py-0.5 rounded ${className}`}
      >
        Calculating...
      </span>
    );
  }

  if (calculatedDistance === undefined) {
    return null;
  }

  // Format the distance in a user-friendly way (minutes/hours)
  const formatDistance = (distance: number) => {
    // Different speed assumptions based on mode and calculation source
    let speedKmh = 40; // Default driving speed

    if (calculationSource === "google") {
      // Google Maps provides road-based distances, so we can use more realistic speeds
      switch (mode) {
        case "walking":
          speedKmh = 5;
          break;
        case "bicycling":
          speedKmh = 15;
          break;
        case "transit":
          speedKmh = 25; // Average for public transport
          break;
        case "driving":
        default:
          speedKmh = 35; // More realistic for city driving with traffic
          break;
      }
    } else {
      // Haversine gives straight-line distance, so adjust speeds accordingly
      switch (mode) {
        case "walking":
          speedKmh = 4;
          break;
        case "bicycling":
          speedKmh = 12;
          break;
        case "transit":
          speedKmh = 20;
          break;
        case "driving":
        default:
          speedKmh = 30; // Conservative for straight-line distance
          break;
      }
    }

    const totalMinutes = Math.round((distance / speedKmh) * 60);

    if (totalMinutes < 1) return "< 1 min away";
    if (totalMinutes === 1) return "1 min away";
    if (totalMinutes < 60) return `${totalMinutes} mins away`;
    if (totalMinutes > 60 * 1.5) return "Far away";

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) return `${hours} hr${hours > 1 ? "s" : ""} away`;
    return `${hours} hr${hours > 1 ? "s" : ""} & ${minutes} min${minutes > 1 ? "s" : ""} away`;
  };

  const formattedDistance = formatDistance(calculatedDistance);

  // Render either as badge or text based on variant
  if (variant === "badge") {
    return (
      <span
        className={`bg-black/60 text-white text-xs font-medium px-1.5 py-0.5 rounded ${className}`}
      >
        {formattedDistance}
      </span>
    );
  }

  return (
    <span className={`text-sm text-text-light ${className}`}>
      {showIcon && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 inline-block mr-1"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
            clipRule="evenodd"
          />
        </svg>
      )}
      {formattedDistance}
    </span>
  );
}
